import { createContext, useContext, useEffect, useState, useRef, ReactNode, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { hasFeatureAccess, hasProgramAccess as hasProgramAccessLib, type Feature, type PackageTier } from "@/lib/planFeatures";
import { useImpersonation, impersonationPlanName } from "@/contexts/ImpersonationContext";
import {
  DEFAULT_GRACE_CONFIG,
  GRACE_SETTING_KEYS,
  NO_GRACE,
  computeGraceWindow,
  graceAllows,
  parseGraceConfig,
  type GraceConfig,
  type GraceWindow,
} from "@/lib/gracePeriod";

export interface SubscriptionData {
  subscribed: boolean;
  plan_name: string | null;
  subscription_end: string | null;
  /** Optional lifecycle hint. Only set by admin impersonation today. */
  status?: "active" | "canceled" | "expired";
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscription: SubscriptionData | null;
  subscriptionLoading: boolean;
  isPremium: boolean;
  planName: string | null;
  /** All active package tiers held by the user (multi-package support). */
  activePackages: PackageTier[];
  /** All program UUIDs the user can train against right now. */
  activeProgramIds: string[];
  hasAccess: (feature: Feature) => boolean;
  /** Üyelik bitti ama ek süre (grace period) devam ediyor mu? */
  grace: GraceWindow;
  /** Üyeliği süresi dolmuş kullanıcıda son bitiş tarihi (ISO), yoksa null. */
  membershipExpiredAt: string | null;
  /** Site ayarlarından okunan ek süre yapılandırması. */
  graceConfig: GraceConfig;

  hasProgramAccess: (programId: string | null | undefined) => boolean;
  refreshSubscription: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Oturum kendiliğinden sona erdiğinde (token yenilenemedi/süre doldu) true olur. */
  sessionExpired: boolean;
  /** Yeniden giriş ekranı kapatıldığında bayrağı temizler. */
  clearSessionExpired: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  subscription: null,
  subscriptionLoading: false,
  isPremium: false,
  planName: null,
  activePackages: [],
  activeProgramIds: [],
  hasAccess: () => false,
  grace: NO_GRACE,
  membershipExpiredAt: null,
  graceConfig: DEFAULT_GRACE_CONFIG,
  hasProgramAccess: () => false,
  refreshSubscription: async () => {},
  signOut: async () => {},
  sessionExpired: false,
  clearSessionExpired: () => {},
});

export const useAuth = () => useContext(AuthContext);

interface SubRow {
  plan_name: string;
  program_id: string | null;
  status: string;
  current_period_end: string | null;
  canceled_at: string | null;
}

const normalizeTier = (raw: string | null | undefined): PackageTier | null => {
  if (!raw) return null;
  const u = raw.toUpperCase();
  return u === "STARTER" || u === "PREMIUM" || u === "ELITE" ? (u as PackageTier) : null;
};

const withTimeout = async <T,>(promise: PromiseLike<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`timeout after ${ms}ms`)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [realUser, setRealUser] = useState<User | null>(null);
  const [realSession, setRealSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [realSubscription, setRealSubscription] = useState<SubscriptionData | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subRows, setSubRows] = useState<SubRow[]>([]);
  const [graceConfig, setGraceConfig] = useState<GraceConfig>(DEFAULT_GRACE_CONFIG);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [sessionExpired, setSessionExpired] = useState(false);
  // Kullanıcının daha önce oturumu var mıydı? (kendiliğinden düşen oturumu ayırt etmek için)
  const hadSessionRef = useRef(false);
  // Kullanıcının kendi tıkladığı çıkışta "oturum süresi doldu" ekranı gösterme.
  const manualSignOutRef = useRef(false);

  const { isImpersonating, role: impersonationRole, status: impersonationStatus, endsInDays: impersonationEndsInDays } = useImpersonation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const fetchActiveRows = useCallback(async (uid: string | undefined) => {
    if (!uid) { setSubRows([]); return; }
    try {
      const { data, error } = await withTimeout(
        supabase
          .from("subscriptions")
          .select("plan_name, program_id, status, current_period_end, canceled_at")
          .eq("user_id", uid),
        4_000,
      );
      if (!error) setSubRows((data ?? []) as SubRow[]);
    } catch {
      setSubRows([]);
    }
  }, []);

  // Ek süre (grace period) ayarlarını site_settings'ten oku.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await withTimeout(
          supabase
            .from("site_settings")
            .select("setting_key, setting_value")
            .in("setting_key", Object.values(GRACE_SETTING_KEYS)),
          4_000,
        );
        if (cancelled || error || !data) return;
        const map: Record<string, string | null> = {};
        for (const row of data as { setting_key: string; setting_value: string | null }[]) {
          map[row.setting_key] = row.setting_value;
        }
        setGraceConfig(parseGraceConfig(map));
      } catch {
        /* varsayılan yapılandırma ile devam */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Ek süre sayacını canlı tut (saatte bir yeniden değerlendir).
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const fetchSubscription = useCallback(async (currentSession: Session | null) => {
    if (!currentSession) {
      setRealSubscription(null);
      setSubRows([]);
      return;
    }
    setSubscriptionLoading(true);
    try {
      const res = await supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
      });
      if (!res.error && res.data) {
        setRealSubscription(res.data as SubscriptionData);
      }
      await fetchActiveRows(currentSession.user.id);
    } catch {
      // silently fail
    } finally {
      setSubscriptionLoading(false);
    }
  }, [fetchActiveRows]);

  const refreshSubscription = useCallback(async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    await fetchSubscription(currentSession);
  }, [fetchSubscription]);

  useEffect(() => {
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setRealSession(session);
        setRealUser(session?.user ?? null);
        setLoading(false);
        // Oturum kapandıysa veya token yenileme başarısız olduysa state'i temizle.
        const sessionLost =
          event === "SIGNED_OUT" ||
          ((event === "TOKEN_REFRESHED" || event === "USER_UPDATED") && !session);
        if (sessionLost || !session) {
          setRealSubscription(null);
          setSubRows([]);
          queryClient.clear();
          if (sessionLost) {
            const wasSignedIn = hadSessionRef.current;
            const manual = manualSignOutRef.current;
            manualSignOutRef.current = false;
            hadSessionRef.current = false;
            if (wasSignedIn && !manual) {
              // Oturum kendiliğinden düştü: yeniden giriş ekranını göster,
              // yönlendirmeyi SessionExpiredDialog güvenli şekilde yapar.
              setSessionExpired(true);
              return;
            }
            const path = window.location.pathname;
            if (path !== "/") navigate("/", { replace: true });
          }
          return;
        }
        hadSessionRef.current = true;
        setSessionExpired(false);
        setTimeout(() => fetchSubscription(session), 0);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) hadSessionRef.current = true;
      setRealSession(session);
      setRealUser(session?.user ?? null);
      setLoading(false);
      fetchSubscription(session);
    });

    return () => authSubscription.unsubscribe();
  }, [fetchSubscription]);

  useEffect(() => {
    if (!realSession) return;
    const interval = setInterval(() => fetchSubscription(realSession), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [realSession, fetchSubscription]);

  // Faturalandırma akışı (Stripe webhook / manuel onay) üyeliği güncellediğinde
  // tier & entitlement bilgisini anında tazele — 5 dakikalık döngüyü beklemeden.
  useEffect(() => {
    const uid = realUser?.id;
    if (!uid) return;
    const channel = supabase
      .channel(`entitlements-${uid}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${uid}` },
        () => { void refreshSubscription(); },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "premium_requests", filter: `user_id=eq.${uid}` },
        () => { void refreshSubscription(); },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [realUser?.id, refreshSubscription]);



  // Erişim jetonunun süresi dolduysa sessizce yenilemeyi dene; başarısızsa
  // yeniden giriş ekranını tetikle (sekme uyandığında da kontrol edilir).
  useEffect(() => {
    if (!realSession) return;
    let cancelled = false;
    const check = async () => {
      const expiresAt = realSession.expires_at ? realSession.expires_at * 1000 : null;
      if (!expiresAt || expiresAt - Date.now() > 60_000) return;
      const { data, error } = await supabase.auth.refreshSession();
      if (cancelled) return;
      if (error || !data.session) setSessionExpired(true);
    };
    const id = setInterval(check, 30_000);
    const onFocus = () => { void check(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [realSession]);

  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  const signOut = async () => {
    manualSignOutRef.current = true;
    setSessionExpired(false);
    await supabase.auth.signOut();
    setRealSubscription(null);
    setSubRows([]);
    // Tüm cache'leri temizle (kullanıcıya özel veriler dahil) ve ana sayfaya dön.
    queryClient.clear();
    navigate("/", { replace: true });
  };

  const activeRows = useMemo(() => subRows.filter((r) => r.status === "active"), [subRows]);

  /** Süresi dolmuş/iptal edilmiş üyeliklerden ek süre penceresini hesapla. */
  const realGrace = useMemo<GraceWindow>(() => {
    if (activeRows.length > 0) return NO_GRACE;
    const endedAt = subRows
      .filter((r) => r.status !== "active")
      .map((r) => r.current_period_end ?? r.canceled_at);
    return computeGraceWindow(endedAt, graceConfig, new Date(nowTick));
  }, [subRows, activeRows, graceConfig, nowTick]);

  /** Ek süre boyunca devam eden program erişimleri. */
  const graceProgramIds = useMemo(() => {
    if (!realGrace.active || !graceAllows(graceConfig, "training_premium")) return [] as string[];
    return Array.from(
      new Set(
        subRows
          .filter((r) => r.status !== "active")
          .map((r) => r.program_id)
          .filter(Boolean) as string[],
      ),
    );
  }, [realGrace.active, graceConfig, subRows]);

  const effective = useMemo(() => {
    if (!isImpersonating) {
      const isPremium = realSubscription?.subscribed === true;
      const activePackages = Array.from(
        new Set(activeRows.map((r) => normalizeTier(r.plan_name)).filter(Boolean) as PackageTier[]),
      );
      const activeProgramIds = Array.from(
        new Set(activeRows.map((r) => r.program_id).filter(Boolean) as string[]),
      );
      return {
        user: realUser,
        session: realSession,
        subscription: realSubscription,
        isPremium,
        planName: isPremium ? realSubscription?.plan_name ?? null : null,
        activePackages,
        activeProgramIds,
      };
    }

    if (impersonationRole === "anonymous") {
      return {
        user: null as User | null,
        session: null as Session | null,
        subscription: null as SubscriptionData | null,
        isPremium: false,
        planName: null as string | null,
        activePackages: [] as PackageTier[],
        activeProgramIds: [] as string[],
      };
    }

    const planName = impersonationPlanName(impersonationRole);
    const endDate = new Date(Date.now() + impersonationEndsInDays * 86400_000).toISOString();
    const isExpired = impersonationStatus === "expired" || impersonationEndsInDays < 0;
    const subscribed = !isExpired;
    const isPaidTier = subscribed && planName === "PREMIUM";
    const tier = normalizeTier(planName);
    return {
      user: realUser,
      session: realSession,
      subscription: {
        subscribed,
        plan_name: subscribed ? planName : null,
        subscription_end: endDate,
        status: isExpired ? "expired" : impersonationStatus,
      } as SubscriptionData,
      isPremium: isPaidTier,
      planName: subscribed ? planName : null,
      activePackages: subscribed && tier ? [tier] : [],
      activeProgramIds: [] as string[],
    };
  }, [isImpersonating, impersonationRole, impersonationStatus, impersonationEndsInDays, realUser, realSession, realSubscription, activeRows]);

  const grace: GraceWindow = isImpersonating ? NO_GRACE : realGrace;

  /**
   * Üyeliği tamamen sona ermiş kullanıcıda son bitiş tarihi.
   * Ek süre kapalı olsa da hesaplanır; bilgilendirme ekranı bunu kullanır.
   */
  const realMembershipExpiredAt = useMemo<string | null>(() => {
    if (activeRows.length > 0) return null;
    const times = subRows
      .filter((r) => r.status !== "active")
      .map((r) => r.current_period_end ?? r.canceled_at)
      .map((iso) => (iso ? new Date(iso).getTime() : NaN))
      .filter((t) => Number.isFinite(t) && t <= nowTick);
    if (!times.length) return null;
    return new Date(Math.max(...times)).toISOString();
  }, [subRows, activeRows, nowTick]);

  const membershipExpiredAt = isImpersonating
    ? (effective.isPremium ? null : effective.subscription?.subscription_end ?? null)
    : realMembershipExpiredAt;

  const hasAccess = useCallback(
    (feature: Feature) => {
      const allowed = effective.activePackages.length > 0
        ? hasFeatureAccess(effective.activePackages, feature)
        : hasFeatureAccess(effective.planName, feature);
      if (allowed) return true;
      // Üyelik bitti ama ek süre sürüyorsa yalnızca izin verilen sayfalar açık kalır.
      return grace.active && graceAllows(graceConfig, feature);
    },
    [effective.activePackages, effective.planName, grace.active, graceConfig],
  );

  const hasProgramAccess = useCallback(
    (programId: string | null | undefined) =>
      hasProgramAccessLib(effective.activeProgramIds, programId) ||
      (grace.active && hasProgramAccessLib(graceProgramIds, programId)),
    [effective.activeProgramIds, grace.active, graceProgramIds],
  );

  return (
    <AuthContext.Provider value={{
      user: effective.user,
      session: effective.session,
      loading,
      subscription: effective.subscription,
      subscriptionLoading,
      isPremium: effective.isPremium,
      planName: effective.planName,
      activePackages: effective.activePackages,
      activeProgramIds: effective.activeProgramIds,
      hasAccess,
      grace,
      membershipExpiredAt,
      graceConfig,
      hasProgramAccess,
      refreshSubscription,
      signOut,
      sessionExpired,
      clearSessionExpired,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
